import { useState } from 'react';
import styled from 'styled-components';
import { CaretDown } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { theme } from '@/styles/theme';

const AccordionWrapper = styled.div`
  border: 1px solid ${theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  background: ${theme.colors.white};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.neutral[100]};
  }
`;

const Question = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.black};
  margin: 0;
`;

const IconWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary.main};
`;

const AccordionContent = styled(motion.div)`
  overflow: hidden;
`;

const Answer = styled.div`
  padding: 0 ${theme.spacing.xl} ${theme.spacing.lg} ${theme.spacing.xl};
  color: ${theme.colors.text.darkGray};
  line-height: 1.7;
`;

interface AccordionItemProps {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}

export const AccordionItem = ({ question, answer, defaultOpen = false }: AccordionItemProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <AccordionWrapper>
      <AccordionHeader onClick={() => setIsOpen(!isOpen)}>
        <Question>{question}</Question>
        <IconWrapper
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <CaretDown size={24} weight="bold" />
        </IconWrapper>
      </AccordionHeader>
      <AnimatePresence initial={false}>
        {isOpen && (
          <AccordionContent
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Answer>{answer}</Answer>
          </AccordionContent>
        )}
      </AnimatePresence>
    </AccordionWrapper>
  );
};
